// Spec 30 PR 3 cleanup — /welcome layout.
//
// Two areas:
//   - Main (large, left): the real graph canvas (sigma + force-atlas).
//     Hidden until the first graph_node lands; then it grows as
//     more events arrive.
//   - Aside (right rail): the gen-ui dialogue column. Always visible
//     so the next ask is always next to the canvas.
//
// Strict event-driven contract:
//   - `widget`        → append to the timeline.
//   - `widget_state`  → the ONLY writer to widgetStates.
//   - `graph_node`    → append to graphNodes (deduped on id).
//   - `graph_edge`    → append to graphEdges (deduped on from+to+relation).
//   - all other event kinds → no-ops.
//
// See specs/renketsu-v2/30-pr3-onboarding-cleanup.md.

import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router";
import type {
	ClientWidgetSpec,
	GraphEdgePayload,
	GraphNodePayload,
	TurnStreamEvent,
	WidgetState,
} from "@/components/gen-ui";
import { colorForNodeType } from "@/components/canvas/colors";
import { GenStagger, useTurnStream, WidgetSurface } from "@/components/gen-ui";
import { registerOnboardingClientWidgets } from "@/components/gen-ui/widgets";
import { Surface } from "@/components/glass";
import { GraphCanvas } from "@/components/graph-canvas";
import { analytics } from "@/lib/analytics";
import { useSession } from "@/lib/auth-client";

// Idempotent client-side registration. Runs on first module load.
registerOnboardingClientWidgets();

interface OnboardingSession {
	id: string;
	status: "active" | "complete" | "skipped" | "abandoned";
	createdAt: string;
	updatedAt: string;
	completedAt: string | null;
}

interface OnboardingEvent {
	id: string;
	sequence: number;
	kind: string;
	payload: unknown;
}

interface OnboardingMe {
	id: string;
	email: string;
	name: string | null;
	needsOnboarding: boolean;
}

interface OnboardingSessionResponse {
	session: OnboardingSession;
	events: OnboardingEvent[];
	user: OnboardingMe;
}

async function fetchSession(): Promise<OnboardingSessionResponse> {
	const r = await fetch("/api/onboarding/session", { credentials: "include" });
	if (!r.ok) throw new Error(`onboarding: ${r.status}`);
	return r.json();
}

// Colour-by-type lives in @/components/canvas/colors so the welcome
// canvas and the project graph share one source of truth.

export default function WelcomeView() {
	const session = useSession();
	const navigate = useNavigate();

	const sessionQ = useQuery({
		queryKey: ["onboarding-session"],
		queryFn: fetchSession,
		enabled: !!session.data?.user,
	});

	const [widgets, setWidgets] = useState<ClientWidgetSpec[]>([]);
	const [widgetStates, setWidgetStates] = useState<Record<string, WidgetState>>(
		{},
	);
	const [graphNodes, setGraphNodes] = useState<GraphNodePayload[]>([]);
	const [graphEdges, setGraphEdges] = useState<GraphEdgePayload[]>([]);
	const [bootstrapped, setBootstrapped] = useState(false);

	const sessionId = sessionQ.data?.session.id ?? null;

	// Spec 39: onboarding funnel endpoints. Fire each at most once per mount.
	const onboardingStatus = sessionQ.data?.session.status ?? null;
	const startedFiredRef = useRef(false);
	const completedFiredRef = useRef(false);
	useEffect(() => {
		if (onboardingStatus === "active" && !startedFiredRef.current) {
			startedFiredRef.current = true;
			analytics.capture("onboarding_started", {});
		}
		if (onboardingStatus === "complete" && !completedFiredRef.current) {
			completedFiredRef.current = true;
			analytics.capture("onboarding_completed", {});
		}
	}, [onboardingStatus]);

	// Replay walks the event log in order applying the same
	// invariants the live stream does.
	useEffect(() => {
		if (!sessionQ.data) return;
		const w: ClientWidgetSpec[] = [];
		const s: Record<string, WidgetState> = {};
		const gn: GraphNodePayload[] = [];
		const ge: GraphEdgePayload[] = [];
		for (const ev of sessionQ.data.events) {
			if (ev.kind === "widget_render") {
				const p = ev.payload as {
					widgetId: string;
					widget: ClientWidgetSpec;
				};
				w.push(p.widget);
			} else if (ev.kind === "widget_state_change") {
				const p = ev.payload as { widgetId: string; state: WidgetState };
				s[p.widgetId] = p.state;
			} else if (ev.kind === "graph_node_change") {
				const p = ev.payload as { node: GraphNodePayload };
				gn.push(p.node);
			} else if (ev.kind === "graph_edge_change") {
				const p = ev.payload as { edge: GraphEdgePayload };
				ge.push(p.edge);
			}
		}
		setWidgets(w);
		setWidgetStates(s);
		setGraphNodes(gn);
		setGraphEdges(ge);
	}, [sessionQ.data]);

	const handleEvent = useCallback((event: TurnStreamEvent) => {
		switch (event.kind) {
			case "widget": {
				setWidgets((ws) => {
					const idx = ws.findIndex((wi) => wi.id === event.widget.id);
					if (idx >= 0) {
						const next = ws.slice();
						next[idx] = event.widget;
						return next;
					}
					return [...ws, event.widget];
				});
				break;
			}
			case "widget_state": {
				setWidgetStates((states) => ({
					...states,
					[event.widgetId]: event.state,
				}));
				break;
			}
			case "graph_node": {
				setGraphNodes((ns) => {
					if (ns.some((n) => n.externalId === event.node.externalId)) return ns;
					return [...ns, event.node];
				});
				break;
			}
			case "graph_edge": {
				setGraphEdges((es) => {
					if (
						es.some(
							(e) =>
								e.fromExternalId === event.edge.fromExternalId &&
								e.toExternalId === event.edge.toExternalId &&
								e.relation === event.edge.relation,
						)
					)
						return es;
					return [...es, event.edge];
				});
				break;
			}
			case "tool_call":
			case "tool_result":
			case "message_delta":
			case "done":
				break;
		}
	}, []);

	const turn = useTurnStream({
		sessionId,
		onEvent: handleEvent,
	});

	useEffect(() => {
		if (!sessionQ.data) return;
		if (sessionQ.data.events.length > 0) return;
		if (bootstrapped) return;
		if (sessionQ.data.session.status !== "active") return;
		setBootstrapped(true);
		turn
			.send({ kind: "text", text: "" })
			.catch((e) => console.error("[welcome] bootstrap failed:", e));
	}, [sessionQ.data, bootstrapped, turn]);

	// T4 OAuth round-trip detection. If the github-connect widget is
	// in submitting state on mount, check whether the user has any
	// GitHub installations. If yes, fire a verify turn that ingests
	// the installation node and advances to final_continue.
	const [ghVerifyTried, setGhVerifyTried] = useState(false);
	useEffect(() => {
		if (!sessionQ.data) return;
		if (ghVerifyTried) return;
		const ghState = widgetStates["github-connect"];
		if (ghState?.kind !== "submitting") return;
		setGhVerifyTried(true);

		fetch("/api/github/installations", { credentials: "include" })
			.then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
			.then((data: { installations?: Array<{ id: string }> }) => {
				if (data.installations && data.installations.length > 0) {
					return turn.send({
						kind: "widget",
						action: { widgetId: "github-connect", action: "verify" },
					});
				}
				return undefined;
			})
			.catch((e) => {
				console.warn("[welcome] github verify check failed:", e);
			});
	}, [sessionQ.data, widgetStates, ghVerifyTried, turn]);

	// Spec 33. Gmail OAuth round-trip detection. Mirrors the github
	// pattern above: when the gmail-connect widget is in submitting
	// state on mount, check whether a gmail_account row exists for
	// this user. If yes, fire a verify turn that ingests the account
	// node and advances to final_continue.
	const [gmailVerifyTried, setGmailVerifyTried] = useState(false);
	useEffect(() => {
		if (!sessionQ.data) return;
		if (gmailVerifyTried) return;
		const gmState = widgetStates["gmail-connect"];
		if (gmState?.kind !== "submitting") return;
		setGmailVerifyTried(true);

		fetch("/api/gmail/accounts", { credentials: "include" })
			.then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
			.then((data: { accounts?: Array<{ id: string }> }) => {
				if (data.accounts && data.accounts.length > 0) {
					return turn.send({
						kind: "widget",
						action: { widgetId: "gmail-connect", action: "verify" },
					});
				}
				return undefined;
			})
			.catch((e) => {
				console.warn("[welcome] gmail verify check failed:", e);
			});
	}, [sessionQ.data, widgetStates, gmailVerifyTried, turn]);

	const handleWidgetAction = useCallback(
		(widgetId: string, action: string, values?: unknown) => {
			if (action === "dirty" || action === "reset") return;

			// Special case: GitHub connect. The widget schema doesn't
			// carry the install URL, so the client fetches it on
			// click and opens in a new tab before firing the action.
			// Spec 28 will replace this with a proper round-trip
			// (server returns the URL via tool_call, client navigates
			// same-window, post-install webhook synthesizes a return
			// event).
			if (widgetId === "github-connect" && action === "connect") {
				fetch("/api/github/app", { credentials: "include" })
					.then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
					.then((data: { install_url?: string }) => {
						if (data.install_url) {
							window.open(data.install_url, "_blank", "noopener,noreferrer");
						}
					})
					.catch((e) => console.warn("[welcome] github app info:", e));
			}

			// Spec 33. Same special-case for Gmail — fetch the OAuth
			// URL on click and open it in a new tab.
			if (widgetId === "gmail-connect" && action === "connect") {
				fetch("/api/gmail/connect", { credentials: "include" })
					.then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
					.then((data: { url?: string }) => {
						if (data.url) {
							window.open(data.url, "_blank", "noopener,noreferrer");
						}
					})
					.catch((e) => console.warn("[welcome] gmail oauth url:", e));
			}

			turn
				.send({
					kind: "widget",
					action: { widgetId, action, values },
				})
				.catch((e) => console.error("[welcome] turn failed:", e));
		},
		[turn],
	);

	// Map our SSE payloads into the canvas's normalized shape.
	const canvasNodes = useMemo(
		() =>
			graphNodes.map((n) => ({
				id: n.externalId,
				label: n.label,
				type: n.nodeType,
				source: n.source,
			})),
		[graphNodes],
	);
	const canvasEdges = useMemo(
		() =>
			graphEdges.map((e) => ({
				from: e.fromExternalId,
				to: e.toExternalId,
				relation: e.label ?? e.relation.toLowerCase().replace(/_/g, " "),
			})),
		[graphEdges],
	);

	if (session.isPending) return null;
	if (!session.data?.user) return <Navigate to="/login" replace />;

	if (sessionQ.isPending) {
		return <Surface className="min-h-screen" aria-busy="true" />;
	}

	if (sessionQ.error) {
		return (
			<Surface className="min-h-screen flex items-center justify-center p-4">
				<div className="text-center space-y-3 max-w-sm">
					<div className="text-[13px]">Couldn't load onboarding.</div>
					<div className="text-[11px] text-[var(--color-text-dim)]">
						{(sessionQ.error as Error).message}
					</div>
					<button
						type="button"
						onClick={() => navigate("/", { replace: true })}
						className="text-[12px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
					>
						Open dashboard
					</button>
				</div>
			</Surface>
		);
	}

	const data = sessionQ.data!;
	if (!data.user.needsOnboarding && data.events.length === 0) {
		return <Navigate to="/" replace />;
	}

	const firstName = data.user.name?.split(" ")[0] ?? null;
	const hasGraph = canvasNodes.length > 0;

	return (
		<Surface className="min-h-screen flex">
			{/* Main: real graph canvas. Hidden until the first node lands. */}
			<div className="flex-1 min-w-0 relative">
				{hasGraph ? (
					<GraphCanvas
						nodes={canvasNodes}
						edges={canvasEdges}
						colorForType={colorForNodeType}
						className="absolute inset-0"
					/>
				) : (
					<div className="absolute inset-0 flex items-center justify-center">
						<div className="text-center text-[12px] text-[var(--color-text-dim)]">
							{firstName ? `Welcome, ${firstName}.` : "Welcome."}
						</div>
					</div>
				)}
			</div>

			{/* Aside: the dialogue column. Always visible. */}
			<aside className="w-[480px] shrink-0 border-l border-[var(--color-border)] flex flex-col">
				<div className="flex-1 overflow-y-auto px-6 py-12">
					<div className="space-y-6">
						{widgets.map((w, i) => (
							<GenStagger key={w.id} index={i}>
								<WidgetSurface
									widget={w}
									externalState={widgetStates[w.id]}
									onAction={handleWidgetAction}
								/>
							</GenStagger>
						))}
					</div>
				</div>
			</aside>
		</Surface>
	);
}
